/**
 ⚠️ Design System — Protected file
 Do not edit this file without prior approval from the Design System team.
 */

import { AccessibilityInfo, NativeSyntheticEvent, Platform } from 'react-native'
import { v4 } from 'uuid'

import { createStore } from 'libs/store/createStore'
import { SnackBarProps } from 'ui/designSystem/Snackbar/SnackBar'

type Snackbar = {
  label: string
  type: 'success' | 'error'
  animationDuration: number
}

type State = { snackbars: Record<string, Snackbar> }

type SnackbarWithAccessibilityProps = SnackBarProps & {
  accessibilityLabel: string
  accessibilityHint: string
  accessibilityActions: { name: 'activate'; label: 'Fermer' }[]
  onAccessibilityTap: () => void
  onAccessibilityAction: (event: NativeSyntheticEvent<{ actionName: string }>) => void
}

const defaultState: State = { snackbars: {} }

export const SHORT_TEXT_ANIMATION_DURATION_IN_MS = 5000
export const LONG_TEXT_ANIMATION_DURATION_IN_MS = 10000
export const LONG_TEXT_CHARACTER_LENGTH = 120
export const EXIT_THRESHOLD_IN_MS = 100 // keep snackbar a bit longer than the progress bar to avoid the progress bar animation after unmount

const getAnimationDuration = (label: string) =>
  label.length < LONG_TEXT_CHARACTER_LENGTH
    ? SHORT_TEXT_ANIMATION_DURATION_IN_MS
    : LONG_TEXT_ANIMATION_DURATION_IN_MS

export const snackBarStore = createStore({
  name: 'snackbar-store',
  defaultState,
  actions: (set) => ({
    showSuccessSnackBar: (label: string) => {
      snackBarStore.actions.open(label, 'success')
    },
    showErrorSnackBar: (label: string) => {
      snackBarStore.actions.open(label, 'error')
    },
    open: (label: string, type: 'success' | 'error') => {
      const id = v4()
      const animationDuration = getAnimationDuration(label)

      // On web, the accessibility role automatically announces the snackbar.
      // Calling announceForAccessibility as well would result in duplicate announcements.
      if (Platform.OS !== 'web') AccessibilityInfo.announceForAccessibility(label)

      set((state) => ({
        snackbars: {
          ...state.snackbars,
          [id]: { label, type, animationDuration },
        },
      }))
      const timeout = setTimeout(() => {
        snackBarStore.actions.close(id)
        clearTimeout(timeout)
      }, animationDuration + EXIT_THRESHOLD_IN_MS)
    },
    close: (id: string) => {
      set((state) => {
        if (!snackBarStore.selectors.selectById(id)) {
          return state
        }

        const newState = { ...state.snackbars }
        delete newState[id]
        return { snackbars: newState }
      })
    },
    reset: () => set(() => defaultState),
  }),
  selectors: {
    selectById: (id: string) => (state) => state.snackbars[id],
    selectSnackbarProps:
      () =>
      (state): SnackbarWithAccessibilityProps[] => {
        const snackbars = state.snackbars
        return Object.entries(snackbars).map(([id, value]) => {
          const onClose = () => snackBarActions.close(id)
          return {
            onClose,
            id,
            ...value,
            accessibilityLabel: value.label,
            accessibilityHint: 'Appuyer deux fois pour fermer',
            accessibilityActions: [{ name: 'activate' as const, label: 'Fermer' }],
            onAccessibilityTap: onClose,
            onAccessibilityAction: (event: NativeSyntheticEvent<{ actionName: string }>) => {
              if (event.nativeEvent.actionName === 'activate') onClose()
            },
          }
        })
      },
  },
})

export const snackBarActions = snackBarStore.actions
export const { showErrorSnackBar, showSuccessSnackBar } = snackBarActions
export const { useSnackbarProps } = snackBarStore.hooks
export type { SnackbarWithAccessibilityProps }
